// The format painter's arming ("Copy style"), shared by the slides bar (slides/SlidesEditor.tsx,
// AB#323) and the prose bar (EditorToolbar.tsx, AB#331): a click arms it once (the next target gets
// the style, then it disarms), a double-click arms it sticky (every target, until Esc or the button
// again), a click while armed disarms. What is captured and how it is pasted stay each host's own —
// the hook only holds the source, local UI state that never reaches the Y.Doc nor the awareness.
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { useLang, type Key } from '../../i18n'

/** The armed painter: the copied style, and whether it stays armed after a paint. */
export interface ArmedPainter<S> {
  src: S
  sticky: boolean
}

export interface FormatPainterOptions {
  /** Announce to screen readers. */
  say: (text: string) => void
  /** Whether Esc pressed on this target is the painter's to take (the page body always is). */
  owns: (target: Node) => boolean
  /** What arming once / sticky announces (the host names what it paints: an object, a selection). */
  sayOn: Key
  saySticky: Key
}

export function useFormatPainter<S>(opts: FormatPainterOptions) {
  const { t } = useLang()
  const [painter, setPainter] = useState<ArmedPainter<S> | null>(null)
  const painterRef = useRef(painter)
  painterRef.current = painter
  const optsRef = useRef(opts)
  optsRef.current = opts

  /** The button: a click arms it once (again: disarms), a double-click arms it until Esc. Nothing
   *  to capture (null) leaves it as it is. */
  const onClick = (e: MouseEvent<HTMLElement>, capture: () => S | null): void => {
    if (e.detail >= 2) {
      const src = painter?.src ?? capture()
      if (!src) return
      setPainter({ src, sticky: true })
      opts.say(t(opts.saySticky))
      return
    }
    if (painter) {
      setPainter(null)
      opts.say(t('fp_off'))
      return
    }
    const src = capture()
    if (!src) return
    setPainter({ src, sticky: false })
    opts.say(t(opts.sayOn))
  }

  // Esc while the painter is armed only disarms it: it never reaches the editor (it neither leaves
  // the edited text nor the selection). Caught in the capture phase, before the editor's own keys.
  const armed = painter !== null
  useEffect(() => {
    if (!armed) return
    const onKey = (e: globalThis.KeyboardEvent): void => {
      const target = e.target as Node
      if (e.key !== 'Escape' || (target !== document.body && !optsRef.current.owns(target))) return
      e.preventDefault()
      e.stopPropagation()
      setPainter(null)
      optsRef.current.say(t('fp_off'))
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [armed, t])

  return { painter, setPainter, painterRef, onClick }
}
